import React, { useEffect, useState } from 'react';
import { BiCartAlt } from 'react-icons/bi';
import ReactGA from 'react-ga4';
import moment from 'moment';
import NothingHere from 'assets/vectors/NothingHere.svg';
import axios from 'config/axios';
import Helmet from 'components/Helmet';
import ButtonLink from 'components/ButtonLink';
import Dropdown from 'components/Dropdown';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { initializeCart } from 'store/cart';
import { setAlert } from 'store/error';
import { Test } from 'global/types';
import './index.css';

function Cart() {
    const [availableTests, setAvailableTests] = useState<Test[]>([]);
    const [total, setTotal] = useState(0);

    const {
        isUpdatingCart,
        bundles,
        courses,
        tests
    } = useAppSelector(state => state.cart);
    const dispatch = useAppDispatch();
    
    const numItems = courses.length + tests.filter(test => !test.addOn).length + bundles.reduce((count, bundle) => (
        count + (bundle.numRequired ? 0 : (bundle.courses.length + bundle.tests.length))
    ), 0);

    const refreshCart = () => {
        axios.get('payments/cart/')
            .then(res => {
                // console.log(res.data);
                dispatch(initializeCart({
                    bundles: res.data?.cartBundles,
                    courses: res.data?.cartCourses,
                    tests: res.data?.cartTests
                }));

                setTotal(res.data?.total);
            })
            .catch(error => {
                // console.log(error);
                dispatch(setAlert(error));
            });
    }

    const removeBundleFromCart = (bundleId: string) => {
        axios.post(`payments/cart/remove/bundle/${bundleId}/`)
        .then(res => {
            // console.log(res.data);
            refreshCart();

            if (res.data.price > 0) {
                ReactGA.event('remove_from_cart', {
                    currency: 'USD',
                    value: res.data.price,
                    items: [{
                        item_id: bundleId,
                        item_name: res.data.name
                    }]
                });
            }
        })
        .catch(error => {
            // console.log(error);
            dispatch(setAlert(error));
        });
    }

    const removeCourseFromCart = (courseUrl: string) => {
        axios.post(`payments/cart/remove/course/${courseUrl}/`)
            .then(res => {
                // console.log(res.data);
                refreshCart();

                if (res.data.tuition > 0) {
                    ReactGA.event('remove_from_cart', {
                        currency: 'USD',
                        value: res.data.tuition,
                        items: [{
                            item_id: courseUrl,
                            item_name: res.data.name
                        }]
                    });
                }
            })
            .catch(error => {
                // console.log(error);
                dispatch(setAlert(error));
            });
    }

    const removeTestsInCart = (testType: string) => {
        axios.post(`payments/cart/update/tests/`, {
            tests: [],
            testType: testType
        })
            .then(res => {
                // console.log(res.data);
                const { addedTests, removedTests } = res.data;

                refreshCart();

                for (let i = 0; i < addedTests.length; i++) {
                    if (addedTests[i].price > 0) {
                        ReactGA.event('add_to_cart', {
                            currency: 'USD',
                            value: addedTests[i].price,
                            items: [{
                                item_id: addedTests[i].testId,
                                item_name: addedTests[i].name
                            }]
                        });
                    }
                }
                for (let i = 0; i < removedTests.length; i++) {
                    if (removedTests[i].price > 0) {
                        ReactGA.event('remove_from_cart', {
                            currency: 'USD',
                            value: removedTests[i].price,
                            items: [{
                                item_id: removedTests[i].testId,
                                item_name: removedTests[i].name
                            }]
                        });
                    }
                }
            })
            .catch(error => {
                // console.log(error);
                dispatch(setAlert(error));
            });
    }

    const removeTestFromCart = (testId: string) => {
        axios.post(`payments/cart/remove/test/${testId}/`)
            .then(res => {
                // console.log(res.data);
                refreshCart();

                if (res.data.tuition > 0) {
                    ReactGA.event('remove_from_cart', {
                        currency: 'USD',
                        value: res.data.price,
                        items: [{
                            item_id: testId,
                            item_name: res.data.name
                        }]
                    });
                }
            })
            .catch(error => {
                // console.log(error);
                dispatch(setAlert(error));
            });
    }

    useEffect(() => {
        refreshCart();
        // axios.get('tests/digital/')
        //     .then(res => {
        //         setAvailableTests(res.data?.tests);
        //     });
    }, []);

    if (isUpdatingCart) return <></>;

    return (
        <div className='layout'>
            <Helmet
                title='My Cart - The SAT Crash Course'
            />
            <h1 className='cart__header'>
                <BiCartAlt className='cart__headerIcon' />
                My Cart
                {numItems > 0 && <span>
                    &nbsp;- {numItems} item{numItems > 1 ? 's' : ''} added
                </span>}
            </h1>
            <div className='cart'>
                {numItems === 0
                ?   <div className='cart__nothingHere'>
                        <img className='cart__nothingHereVector' src={NothingHere} alt='Nothing here...' />
                        {/* <p className='cart__nothingHereText'>
                            Nothing here yet...
                        </p> */}
                    </div>  
                :   <div className='cart__items'>
                        {bundles.filter(bundle => bundle.tests.length === 0).map(bundle => (
                            <div key={bundle.name} className='cart__cardItem'>
                                <div className='cart__cardItemHeader'>
                                    <h4 className='cart__cardItemName'>
                                        {bundle.name}
                                    </h4>
                                    {bundle.discount
                                    ?   <p className='cart__cardItemDiscount'>
                                            <span>${bundle.courses.reduce((total, { tuition }) => total + tuition, 0).toFixed(2)}</span>
                                            ${bundle.discount.type === 'Percent'
                                                ? (bundle.courses.reduce((total, { tuition }) => total + tuition, 0)*(100 - bundle.discount.value)/100).toFixed(2)
                                                : bundle.discount.type === 'Amount'
                                                ? (bundle.courses.reduce((total, { tuition }) => total + tuition, 0) - bundle.discount.value).toFixed(2)
                                                : bundle.courses.reduce((total, { tuition }) => total + tuition, 0).toFixed(2)
                                            }
                                        </p>
                                    :   <p className='cart__cardItemPrice'>
                                            ${bundle.courses.reduce((total, { tuition }) => total + tuition, 0).toFixed(2)}
                                        </p>
                                    }
                                    <p
                                        className='cart__itemRemove'
                                        onClick={() => removeBundleFromCart(bundle.id)}
                                    >
                                        remove
                                    </p>
                                </div>
                                {bundle.courses.map(course => (
                                    <p className='cart__cardItemRow'>
                                        {course.name}
                                    </p>
                                ))}
                            </div>
                        ))}
                        {courses.map(course => <div key={course.name} className='cart__cardItem'>
                            <div className='cart__cardItemHeader'>
                                <h4 className='cart__cardItemName'>
                                    {course.name}
                                </h4>
                                {course.discount
                                ?   <p className='cart__cardItemDiscount'>
                                        <span>${course.tuition.toFixed(2)}</span>
                                        ${course.discount.type === 'Percent'
                                            ? (course.tuition - course.discount.value*course.tuition/100).toFixed(2)
                                            : course.discount.type === 'Amount'
                                            ? (course.tuition - course.discount.value).toFixed(2)
                                            : course.tuition.toFixed(2)
                                        }
                                    </p>
                                :   <p className='cart__cardItemPrice'>${course.tuition.toFixed(2)}</p>
                                }
                                <p
                                    className='cart__itemRemove'
                                    onClick={() => removeCourseFromCart(course.urlSlug)}
                                >
                                    remove
                                </p>
                            </div>
                            <p className='cart__cardItemRow'>
                            Available for {Math.round(moment.duration(course.period).asMonths())} Months
                            </p>
                        </div>)}
                        {bundles.filter(bundle => bundle.tests?.length > 0).map(bundle => (
                            <div key={bundle.name} className='cart__cardItem'>
                                <div className='cart__cardItemHeader'>
                                    <h4 className='cart__cardItemName'>
                                        {bundle.name}
                                    </h4>
                                    {bundle.discount
                                    ?   <p className='cart__cardItemDiscount'>
                                            <span>${bundle.tests.reduce((total, { price }) => total + price, 0).toFixed(2)}</span>
                                            ${bundle.discount.type === 'Percent'
                                                ? (bundle.tests.reduce((total, { price }) => total + price, 0)*(100 - bundle.discount.value)/100).toFixed(2)
                                                : bundle.discount.type === 'Amount'
                                                ? (bundle.tests.reduce((total, { price }) => total + price, 0) - bundle.discount.value).toFixed(2)
                                                : bundle.tests.reduce((total, { price }) => total + price, 0).toFixed(2)
                                            }
                                        </p>
                                    :   <p className='cart__cardItemPrice'>
                                            ${bundle.tests.reduce((total, { price }) => total + price, 0).toFixed(2)}
                                        </p>
                                    }
                                    <p
                                        className='cart__itemRemove'
                                        onClick={() => removeBundleFromCart(bundle.id)}
                                    >
                                        remove
                                    </p>
                                </div>
                                {bundle.tests.map(test => (
                                    <p className='cart__cardItemRow'>
                                        {test.name}
                                    </p>
                                ))}
                            </div>
                        ))}
                        {tests.filter(test => bundles.every(bundle => (
                            !bundle.tests.find(bundleTest => bundleTest.testId === test.testId)
                        ))).map(test => (

                        // {tests.map(test => (
                            <div key={test.name} className='cart__cardItem'>
                                <div className='cart__cardItemHeader'>
                                    <h4 className='cart__cardItemName'>
                                        {test.name}
                                    </h4>
                                    {test.discount
                                        ?   <p className='cart__cardItemDiscount'>
                                                <span>${test.price.toFixed(2)}</span>
                                                ${test.discount.type === 'Percent'
                                                    ? (test.price - test.discount.value*test.price/100).toFixed(2)
                                                    : test.discount.type === 'Amount'
                                                    ? (test.price - test.discount.value).toFixed(2)
                                                    : test.price.toFixed(2)
                                                }
                                            </p>
                                        :   <p className='cart__cardItemPrice'>${test.price.toFixed(2)}</p>
                                    }
                                    {!test.addOn &&
                                        <p
                                            className='cart__itemRemove'
                                            onClick={() => removeTestFromCart(test.testId)}
                                        >
                                            remove
                                        </p>
                                    }
                                </div>
                                <p className='cart__cardItemRow'>
                                    {test.type}
                                </p>
                            </div>
                        ))}
                        <div className='cart__cardTotal'>
                            <p>Your Total</p>
                            <b>${total.toFixed(2)}</b>
                        </div>
                        <ButtonLink 
                            className='cart__checkoutButton'
                            to='/checkout'
                        >
                            CHECKOUT
                        </ButtonLink>
                    </div>
                }
            </div>
        </div>
    );
}

export default Cart;